import React from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Templates from './Templates';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';
// Custom Routes
import {
  Login,
  PatientLogin,
  ReportVerification,
  CriticalAcknowledgement,
  FacilityManagement,
  AddFacility,
  UserManagement,
  AddUser,
  FacilityView,
  ViewProfile,
  ViewFacilityDetails,
  AdminDashboard,
  BillingReconciliation,
  MachineManagement,
  Configuration,
  RisConfiguration,
  EscalationMonitor,
  ReportDeliveryAnalytics,
  CriticalResultsDashboard,
  CriticalResultsAlertsDrillDown,
  AcknowledgementAuditTrail,
  CollectionReconciliation,
  OperationalDashboard,
  StatewideKPIs,
  AiDashboard,
  NotificationDelivery,
  PeerReviewAnalytics,
  PeerReviewAnalyticsCases,
  PeerReview,
} from '../custom/pages';
import ABHAConsents from './ABHAConsents';
import { ProtectedRoute } from '../custom/components';

function RedirectToAnalyticsCollection() {
  return (
    <Navigate
      to="/analytics/collection-reconciliation"
      replace
    />
  );
}

function RedirectToReportDelivery() {
  return (
    <Navigate
      to="/analytics/report-delivery"
      replace
    />
  );
}

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;
  const [searchParams] = useSearchParams();
  const isLinkExpired = searchParams.get('reason') === 'expired';

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>
          {isLinkExpired
            ? 'This patient access link has expired.'
            : 'One or more of the requested studies are not available at this time.'}
        </h4>
        {isLinkExpired ? (
          <>
            <p className="mt-2">
              The link is no longer valid. Please contact the facility if you need access again.
            </p>
            <p className="mt-4 text-sm opacity-90">
              If the facility has extended the validity window, reopen the link from your
              SMS/WhatsApp message rather than retrying from this page.
            </p>
          </>
        ) : (
          showStudyList && (
            <p className="mt-2">
              Return to the{' '}
              <Link
                className="text-primary-light"
                to="/"
              >
                study list
              </Link>{' '}
              to select a different study to view.
            </p>
          )
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/login`,
    children: Login,
    private: false,
  },
  {
    path: `/patient/login`,
    children: PatientLogin,
    private: false,
  },
  {
    path: `/report/view/:studyInstanceUid`,
    children: ReportVerification,
    private: false,
  },
  {
    path: `/POCTSR/T/:token`,
    children: CriticalAcknowledgement,
    private: false,
  },
  {
    path: `/facilities`,
    children: FacilityManagement,
    private: true,
  },
  {
    path: `/facilities/add`,
    children: AddFacility,
    private: true,
  },
  {
    path: `/facility-view`,
    children: FacilityView,
    private: true,
  },
  {
    path: `/users`,
    children: UserManagement,
    private: true,
  },
  {
    path: `/users/add`,
    children: AddUser,
    private: true,
  },
  {
    path: `/profile`,
    children: ViewProfile,
    private: true,
  },
  {
    path: `/facility-details`,
    children: ViewFacilityDetails,
    private: true,
  },
  {
    path: `/admin-dashboard`,
    children: AdminDashboard,
    private: true,
  },
  {
    path: `/billing-reconciliation`,
    children: RedirectToAnalyticsCollection,
    private: true,
  },
  {
    path: `/analytics/collection-reconciliation`,
    children: CollectionReconciliation,
    private: true,
  },
  {
    path: `/analytics/statewide-kpis`,
    children: StatewideKPIs,
    private: true,
  },
  {
    path: `/analytics/operational`,
    children: OperationalDashboard,
    private: true,
  },
  {
    path: `/analytics/ai-dashboard`,
    children: AiDashboard,
    private: true,
  },
  {
    path: `/analytics/report-delivery`,
    children: ReportDeliveryAnalytics,
    private: true,
  },
  {
    path: `/analytics/notification-delivery`,
    children: NotificationDelivery,
    private: true,
  },
  {
    path: `/analytics/peer-review/cases`,
    children: PeerReviewAnalyticsCases,
    private: true,
  },
  {
    path: `/analytics/peer-review`,
    children: PeerReviewAnalytics,
    private: true,
  },
  {
    path: `/report-delivery-analytics`,
    children: RedirectToReportDelivery,
    private: true,
  },
  {
    path: `/configuration`,
    children: Configuration,
    private: true,
  },
  {
    path: `/ris-configuration`,
    children: RisConfiguration,
    private: true,
  },
  {
    path: `/escalation-monitor`,
    children: EscalationMonitor,
    private: true,
  },
  {
    path: `/machines`,
    children: MachineManagement,
    private: true,
  },
  {
    path: `/peer-reviews`,
    children: PeerReview,
    private: true,
  },
  {
    path: `/abha-consents`,
    children: ABHAConsents,
    private: true,
  },
  {
    path: `/critical-results-dashboard`,
    children: CriticalResultsDashboard,
    private: true,
  },
  {
    path: `/critical-results-dashboard/:categorySlug`,
    children: CriticalResultsAlertsDrillDown,
    private: true,
  },
  {
    path: `/acknowledgement-audit-trail`,
    children: AcknowledgementAuditTrail,
    private: true,
  },
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkList, servicesManager, extensionManager },
  };

  const TemplatesRoute = {
    path: '/templates',
    children: Templates,
    private: true,
    props: { servicesManager, extensionManager },
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    TemplatesRoute,
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary context={`Route ${route.path}`}>
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are protected by default except public routes like login
  // Custom ProtectedRoute wrapper handles authentication check
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        // Public routes have private: false explicitly set (like /login)
        const isPublicRoute = route.private === false;

        return (
          <Route
            key={i}
            path={route.path}
            element={
              isPublicRoute ? (
                // Public route (like /login) - no authentication required
                <RouteWithErrorBoundary route={route} />
              ) : (
                // Protected route - requires authentication
                <ProtectedRoute>
                  <RouteWithErrorBoundary route={route} />
                </ProtectedRoute>
              )
            }
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
